/**
 * Custom Hook für OCR (Optical Character Recognition)
 * 
 * Verwendet Tesseract.js für clientseitige Texterkennung aus Bildern
 */

import { useState, useCallback, useRef } from 'react';
import { createWorker } from 'tesseract.js';

/** Clamp Wert auf 0–255 */
function clamp(v) {
  return Math.max(0, Math.min(255, Math.round(v)));
}

/** Graustufen (in-place, R=G=B) */
function applyGrayscale(data) {
  for (let i = 0; i < data.length; i += 4) {
    const g = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    data[i] = data[i + 1] = data[i + 2] = clamp(g);
  }
}

/** Helligkeit und Kontrast (in-place) */
function applyBrightnessContrast(data, contrast = 1.2, brightness = 0) {
  const intercept = 128 * (1 - contrast);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp(data[i] * contrast + intercept + brightness);
    data[i + 1] = clamp(data[i + 1] * contrast + intercept + brightness);
    data[i + 2] = clamp(data[i + 2] * contrast + intercept + brightness);
  }
}

/** Leichtes Entrauschen: 3x3 Box-Blur (nutzt Kopie als Quelle) */
function applyDenoise(data, width, height) {
  const copy = new Uint8ClampedArray(data);
  const w = width;
  const h = height;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      let r = 0, g = 0, b = 0, n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const i = ((y + dy) * w + (x + dx)) * 4;
          r += copy[i]; g += copy[i + 1]; b += copy[i + 2]; n++;
        }
      }
      const i = (y * w + x) * 4;
      data[i] = r / n; data[i + 1] = g / n; data[i + 2] = b / n;
    }
  }
}

/** Schärfung (Unsharp-Mask, in-place) */
function applySharpen(data, width, height, amount = 0.6) {
  const blurred = new Uint8ClampedArray(data.length);
  const w = width, h = height;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      let r = 0, g = 0, b = 0, n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const i = ((y + dy) * w + (x + dx)) * 4;
          r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
        }
      }
      const i = (y * w + x) * 4;
      blurred[i] = r / n; blurred[i + 1] = g / n; blurred[i + 2] = b / n; blurred[i + 3] = data[i + 3];
    }
  }
  for (let i = 0; i < data.length; i += 4) {
    const s = data[i] + amount * (data[i] - blurred[i]);
    data[i] = data[i + 1] = data[i + 2] = clamp(s);
  }
}

/** Otsu-Binarisierung: Schwarz/Weiß für klare Texttrennung (Tesseract: schwarzer Text auf weiß) */
function applyOtsuBinarization(data, width, height) {
  const hist = new Array(256).fill(0);
  const total = width * height;
  for (let i = 0; i < data.length; i += 4) {
    hist[data[i]]++;
  }
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, wF = 0;
  let maxVar = 0, threshold = 0;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const varBetween = wB * wF * (mB - mF) * (mB - mF);
    if (varBetween > maxVar) {
      maxVar = varBetween;
      threshold = t;
    }
  }
  for (let i = 0; i < data.length; i += 4) {
    const v = data[i] <= threshold ? 0 : 255;
    data[i] = data[i + 1] = data[i + 2] = v;
  }
}

export function useOCR() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);

  /**
   * Initialisiert Tesseract Worker (lazy loading)
   */
  const initWorker = useCallback(async () => {
    if (!workerRef.current) {
      try {
        const worker = await createWorker('deu+eng', 1, {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setProgress(Math.round(m.progress * 100));
            }
          },
        });
        
        // OCR-Parameter für Fotos: PSM 3 = vollautomatische Seitensegmentierung
        // Keine Whitelist, damit @ € und Sonderzeichen in Fotos erkannt werden
        await worker.setParameters({
          tessedit_pageseg_mode: '3', // Vollautomatisch (besser für Fotos mit variabler Anordnung)
        });
        
        workerRef.current = worker;
      } catch (err) {
        console.error('Fehler beim Initialisieren des OCR Workers:', err);
        throw new Error('OCR konnte nicht initialisiert werden. Bitte lade die Seite neu.');
      }
    }
    return workerRef.current;
  }, []);

  /**
   * Liest EXIF-Orientierung aus Bilddatei
   * Gibt die Orientierung (1-8) zurück, basierend auf EXIF-Daten
   */
  const getImageOrientation = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const view = new DataView(e.target.result);
          if (view.getUint16(0, false) !== 0xFFD8) {
            // Nicht JPEG, Standard-Orientierung
            resolve(1);
            return;
          }
          
          const length = view.byteLength;
          let offset = 2;
          
          while (offset < length) {
            if (view.getUint16(offset, false) !== 0xFFE1) {
              offset += 2;
              continue;
            }
            
            const exifLength = view.getUint16(offset + 2, false);
            if (view.getUint32(offset + 4, false) !== 0x45786966) {
              offset += 2 + exifLength;
              continue;
            }
            
            const little = view.getUint16(offset + 10, false) === 0x4949;
            offset += 6;
            const tiffOffset = view.getUint32(offset, little);
            offset += tiffOffset - 2;
            
            if (offset >= length) {
              resolve(1);
              return;
            }
            
            const tags = view.getUint16(offset, little);
            offset += 2;
            
            for (let i = 0; i < tags; i++) {
              const tagOffset = offset + (i * 12);
              if (tagOffset + 12 > length) break;
              
              if (view.getUint16(tagOffset, little) === 0x0112) {
                const orientation = view.getUint16(tagOffset + 8, little);
                resolve(orientation);
                return;
              }
            }
            
            break;
          }
          
          // Keine EXIF-Orientierung gefunden, Standard
          resolve(1);
        } catch (err) {
          // Bei Fehler Standard-Orientierung
          resolve(1);
        }
      };
      
      reader.onerror = () => {
        resolve(1); // Bei Fehler Standard-Orientierung
      };
      
      // Nur ersten 64KB lesen (EXIF ist normalerweise am Anfang)
      const blob = file.slice(0, 65536);
      reader.readAsArrayBuffer(blob);
    });
  }, []);

  /**
   * Vorverarbeitet ein Bild für bessere OCR-Ergebnisse (besonders Fotos)
   * - Korrigiert EXIF-Orientierung (wichtig für iPhone-Fotos)
   * - Skaliert mit MIN/MAX-Auflösung (1000–2000 px längere Seite)
   * - Graustufen, Kontrast/Helligkeit, Entrauschen, Schärfung, Otsu-Binarisierung
   */
  const preprocessImage = useCallback(async (imageFile) => {
    // EXIF-Orientierung lesen
    const orientation = await getImageOrientation(imageFile);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const imageUrl = URL.createObjectURL(imageFile);

      img.onload = () => {
        try {
          // Berechne neue Dimensionen basierend auf Orientierung
          let width = img.width;
          let height = img.height;
          let rotation = 0;
          let flipX = false;
          let flipY = false;

          // Orientierung korrigieren (EXIF-Standard)
          switch (orientation) {
            case 2:
              flipX = true;
              break;
            case 3:
              rotation = 180;
              break;
            case 4:
              flipY = true;
              break;
            case 5:
              rotation = 90;
              flipX = true;
              [width, height] = [height, width];
              break;
            case 6:
              rotation = 90;
              [width, height] = [height, width];
              break;
            case 7:
              rotation = -90;
              flipX = true;
              [width, height] = [height, width];
              break;
            case 8:
              rotation = -90;
              [width, height] = [height, width];
              break;
            default:
              // Orientierung 1 oder unbekannt - keine Transformation
              break;
          }

          // Auflösung für OCR: Mindestgröße für lesbaren Text, Obergrenze für Performance
          const MIN_DIMENSION = 1000; // Text ~70–100 px Zeichenhöhe
          const MAX_DIMENSION = 2000;
          const maxSide = Math.max(width, height);
          let scale = 1;
          if (maxSide > MAX_DIMENSION) {
            scale = MAX_DIMENSION / maxSide;
          } else if (maxSide < MIN_DIMENSION) {
            scale = MIN_DIMENSION / maxSide; // Hochskalieren für bessere Erkennung
          }
          width = Math.round(width * scale);
          height = Math.round(height * scale);

          canvas.width = width;
          canvas.height = height;

          // Transformationsmatrix anwenden
          ctx.save();
          ctx.translate(width / 2, height / 2);
          
          if (rotation !== 0) {
            ctx.rotate((rotation * Math.PI) / 180);
          }
          
          if (flipX || flipY) {
            ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
          }
          
          // Original-Bilddimensionen für drawImage
          const drawWidth = orientation >= 5 && orientation <= 8 ? img.height : img.width;
          const drawHeight = orientation >= 5 && orientation <= 8 ? img.width : img.height;
          
          ctx.translate(-drawWidth * scale / 2, -drawHeight * scale / 2);
          ctx.drawImage(img, 0, 0, drawWidth * scale, drawHeight * scale);
          ctx.restore();

          // Erweiterte Vorverarbeitung für bessere OCR auf Fotos
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;

          applyGrayscale(data);
          applyBrightnessContrast(data, 1.2, 0);
          applyDenoise(data, width, height);
          applySharpen(data, width, height, 0.6);
          applyOtsuBinarization(data, width, height);

          ctx.putImageData(imageData, 0, 0);

          // Canvas zu Blob konvertieren (PNG für beste Qualität)
          canvas.toBlob(
            (blob) => {
              // URL nach dem Verarbeiten freigeben
              URL.revokeObjectURL(imageUrl);
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Fehler beim Verarbeiten des Bildes'));
              }
            },
            'image/png',
            0.95 // Hohe Qualität
          );
        } catch (err) {
          URL.revokeObjectURL(imageUrl);
          reject(err);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Fehler beim Laden des Bildes'));
      };
      
      img.src = imageUrl;
    });
  }, [getImageOrientation]);

  /**
   * Verarbeitet ein Bild und extrahiert Text
   */
  const recognizeText = useCallback(async (imageFile) => {
    setIsProcessing(true);
    setError("");
    setProgress(0);

    try {
      const worker = await initWorker();
      
      // Bild vorverarbeiten (wichtig für iPhone-Fotos)
      setProgress(10);
      const processedImage = await preprocessImage(imageFile);
      
      setProgress(20);
      // OCR durchführen
      const { data: { text } } = await worker.recognize(processedImage);
      
      setProgress(100);
      return text;
    } catch (err) {
      const errorMsg = err.message || 'Fehler bei der Texterkennung';
      setError(errorMsg);
      console.error('OCR Fehler:', err);
      throw err;
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  }, [initWorker, preprocessImage]);

  /**
   * Öffnet Kamera/Galerie-Dialog
   */
  const openCamera = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  /**
   * Behandelt Datei-Auswahl
   */
  const handleFileSelect = useCallback(async (event, onSuccess) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validierung
    if (!file.type.startsWith('image/')) {
      setError('Bitte wähle ein Bild aus.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB Limit
      setError('Das Bild ist zu groß. Bitte wähle ein Bild unter 10MB.');
      return;
    }

    try {
      const text = await recognizeText(file);
      if (onSuccess && text) {
        onSuccess(text);
      }
    } catch (err) {
      // Fehler wird bereits in recognizeText gesetzt
    } finally {
      // Reset file input, damit derselbe Datei wieder ausgewählt werden kann
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [recognizeText]);

  /**
   * Cleanup: Worker beenden
   */
  const cleanup = useCallback(async () => {
    if (workerRef.current) {
      await workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  return {
    isProcessing,
    progress,
    error,
    fileInputRef,
    openCamera,
    handleFileSelect,
    recognizeText,
    cleanup,
  };
}
